import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Ripple } from "primereact/ripple";
import { confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from "primereact/button";

import DocumentViewModal from "../../../../../Components/DocumentViewModal";
import NoDataCreated from "../../../../../Components/NoDataCreated";
import TextInput from "../../../../../Components/TextInput";
import FileInput from "../../../../../Components/FileInput";
import ErrorPage from "../../../../../Components/ErrorPage";
import PageLoader from "../../../../../Components/PageLoader";
import { goToTop } from "../../../../../Components/goToTop";

import { cashflowCreateAPI, cashflowDeleteAPI, cashflowGetDataApi, cashflowUpdateAPI } from "../../../../../Api/Project/DefaultServices/CashflowsApi";
import { checkFileOthers, checkText } from "../../../../../Utils/Validator";
import { Cashflows } from "../../../../../Controllers/Types/Project/DefaultService/CashFlowInterface";
import { baseURL } from "../../../../../Api/ApiConstant";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../Controllers/Store";
import { hasPermission } from "../../../../../Controllers/Selectors/permissionsSelector";
import TableActionLoader from "../../../../../Components/TableActionLoader";

const CashFlow: React.FC = () => {
    const navigate = useNavigate();
    const toast = useRef<Toast>(null);
    const location = useLocation();
    const projectData = location.state?.data;

    const serviceId = Number(projectData.folder_id);
    const [dataLoading, setDataLoading] = useState<boolean>(false);
    const [initialDataloading, setInitialDataLoading] = useState<boolean>(true);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const isShared: boolean = projectData.sharedBy !== null ? true : false;
    const [loading, setLoading] = useState<boolean>(false);
    const [dataState, setDataState] = useState<'Add' | 'Edit'>('Add');
    const [updateHasChanges, setUpdateHasChanges] = useState<boolean>(false);

    const [showFileViewModal, setShowFileViewModal] = useState(false);
    const [fileUrl, setFileUrl] = useState<string | null>(null);

    const [cashFlowData, setCashFlowData] = useState<Cashflows[]>([]);
    const [showDetailModal, setShowDetailModal] = useState<boolean>(false);
    const [selectedCashFlowData, setSelectedCashFlowData] = useState<Cashflows | null>(null);

    /* ****************************** Form variables **********************************/
    const [documentName, setDocumentName] = useState<string>('');
    const [file, setFile] = useState<File | null>(null);
    const [fileName, setFileName] = useState<string>('');

    /* Error message */
    const [documentNameError, setDocumentNameError] = useState<string>('');
    const [fileError, setFileError] = useState<string>('');
    /* **************************************************************************** */

    const [deleteLoadingId, setdeleteLoadingId] = useState<number | null>(null);

    const useCanEdit = (screen: string, permission: string) => {
        return useSelector((state: RootState) => hasPermission(state, screen, permission));
    };

    const isPermissionCashFlow_progress_options = useCanEdit('Cash Flow', 'progress_options');
    const isPermissionAddCashFlow = useCanEdit('Cash Flow', 'add_cahs_flow');
    const isPermissionEditCashFlow = useCanEdit('Cash Flow', 'edit_cahs_flow');
    const isPermissionDeleteCashFlow = useCanEdit('Cash Flow', 'delete_cahs_flow');


    useEffect(() => {
        cashflowDataGet();
    }, [serviceId]);

    /* ********************************* get data list ************************************************** */
    const cashflowDataGet = async () => {
        if (serviceId) {
            setDataLoading(true);
            const data = await cashflowGetDataApi(Number(serviceId));
            setDataLoading(false);
            setInitialDataLoading(false);

            if (data.status) {
                setCashFlowData(data.data || []);
            } else {
                setErrorMessage(`An error occurred while retrieving ${(projectData.feature_name).toLowerCase()} document data.`);
            }
        }
    }
    /* ********************************* get data list ************************************************** */
    useEffect(() => {
        checkText(documentName, false, setDocumentNameError);
    }, [documentName]);

    useEffect(() => {
        checkFileOthers(file!, false, setFileError);
    }, [file]);

    function checkCredentials(fileRequired: boolean): boolean {
        const isDocumentNameValid = checkText(documentName, true, setDocumentNameError);
        const isFileValid = checkFileOthers(file!, fileRequired, setFileError);

        return isDocumentNameValid && isFileValid;
    }

    /* ********************************* Create ************************************************** */
    const handleCreateDetails = async (e: React.FormEvent) => {
        e.preventDefault();

        if (checkCredentials(true)) {
            setLoading(true);
            const data = await cashflowCreateAPI(Number(serviceId), documentName, file!);
            setLoading(false);

            if (data.statusCode === 200) {
                cashflowDataGet();
                handleCloseDetailModal();

                if (toast.current) {
                    toast.current?.show({
                        severity: "success",
                        summary: "Success",
                        detail: `Document added successfully.`,
                        life: 3000,
                    });
                }
            } else {
                if (toast.current) {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Failed',
                        detail: 'There was an error creating document.',
                        life: 3000
                    });
                }
            }
        }
    }
    /* ********************************* Create ************************************************** */

    /* ********************************* Update ************************************************** */
    const handleUpdateDetails = async (e: React.FormEvent) => {
        e.preventDefault();

        if (checkCredentials(false)) {
            setLoading(true);

            const data = await cashflowUpdateAPI(
                selectedCashFlowData?.id || 0,
                documentName,
                file!
            );

            setLoading(false);

            if (data.statusCode === 200) {
                cashflowDataGet();
                handleCloseDetailModal();

                if (toast.current) {
                    toast.current?.show({
                        severity: "success",
                        summary: "Success",
                        detail: `Document updated successfully.`,
                        life: 3000,
                    });
                }
            } else {
                if (toast.current) {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Failed',
                        detail: 'There was an error updating document.',
                        life: 3000
                    });
                }
            }
        }
    }
    /* ********************************* Update ************************************************** */

    /* ********************************* Delete ************************************************** */
    const handleDeleteData = (dataId: number): void => {
        confirmDialog({
            message: `Are you sure you want to delete this ${projectData.feature_name} document?`,
            header: 'Confirm the deletion',
            icon: 'bi bi-info-circle',
            defaultFocus: 'accept',
            acceptClassName: 'p-button-danger',
            accept: () => deleteData(dataId),
            dismissableMask: true,
            draggable: false,
            resizable: false
        });
    }

    const deleteData = async (dataId: number) => {
        setdeleteLoadingId(dataId);
        const data = await cashflowDeleteAPI(dataId);

        setdeleteLoadingId(null);
        if (data.statusCode === 200) {
            cashflowDataGet();

            if (toast.current) {
                toast.current?.show({
                    severity: "success",
                    summary: "Success",
                    detail: `Document deleted successfully.`,
                    life: 3000,
                });
            }
        } else {
            if (toast.current) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Failed',
                    detail: 'Error deleting document data.',
                    life: 3000
                });
            }
        }
    }
    /* ********************************* Delete ************************************************** */


    /* ***************************** For cashflow ***************************** */
    useEffect(() => {
        if (selectedCashFlowData) {
            if (documentName !== selectedCashFlowData?.document_name) {
                setUpdateHasChanges(true);
            } else if (file !== null) {
                setUpdateHasChanges(true);
            } else {
                setUpdateHasChanges(false);
            }
        }
    }, [selectedCashFlowData, documentName, file])

    const handleCloseDetailModal = () => {
        setShowDetailModal(false);
        handleClearFormFields();
        handleClearFormErrors();
    }

    const handleClearFormFields = () => {
        setDocumentName('');
        setFile(null);
        setFileName('');

        setSelectedCashFlowData(null);
    }

    const handleClearFormErrors = () => {
        setDocumentNameError('');
        setFileError('');
    }

    const handleAddDetails = () => {
        setShowDetailModal(true);
        setDataState('Add');
    }

    const handleEditDetails = (data: Cashflows) => {
        setDataState('Edit');
        setSelectedCashFlowData(data);
        setDocumentName(data?.document_name);

        setShowDetailModal(true);
    }

    /* for file upload */
    const handleFileUpload = (file: File) => {
        setFile(file);
        setFileName(file.name);
    };

    const handleClearFile = () => {
        setFile(null);
        setFileName('');
    };

    /* Modal header */
    const detailModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                {dataState}&nbsp;{(projectData.feature_name).toLowerCase()} document
            </h5>

            <button type="button" className="close_modal_btn p-ripple"
                onClick={handleCloseDetailModal}>
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    );

    /* Modal footer */
    const detailModalFooter = (
        <div className="custom_modal_footer">
            <Button
                label="Close"
                className="custom_btn secondary"
                onClick={handleCloseDetailModal}
            />

            <Button
                label={loading ? 'Processing' : dataState === 'Add' ? 'Save' : 'Update'}
                onClick={dataState === 'Add' ? handleCreateDetails : handleUpdateDetails}
                loading={loading}
                className="custom_btn primary"
                disabled={dataState === 'Edit' && !updateHasChanges}
            />
        </div>
    );
    /* *************************************************************************** */


    /* ***************************** For table view ****************************** */
    /* Table structure */
    const editDataBody = (rowData: Cashflows) => {
        const data = rowData;

        return (
            <button
                className="data_action_btn primary_table_btn p-ripple"
                onClick={() => handleEditDetails(data)}
            >
                <i className="bi bi-pencil-square"></i>
                <Ripple />
            </button>
        )
    }

    const deleteDataBody = (rowData: Cashflows) => {
        const dataId = rowData.id;

        return (
            <React.Fragment>
                {deleteLoadingId === dataId ? (
                    <TableActionLoader />
                ) : (
                    <button
                        className="data_action_btn danger_table_btn p-ripple"
                        onClick={() => handleDeleteData(dataId)}
                    >
                        <i className="bi bi-trash3"></i>
                        <Ripple />
                    </button>
                )}
            </React.Fragment>
        )
    }

    const viewDocumentBody = (rowData: Cashflows) => {
        const filePath = baseURL + rowData.path;

        return (
            <div className="tabel_btn_area justify-content-end justify-content-lg-center mt-1 mb-1">
                <Button
                    label="View"
                    className="tabel_btn view_btn"
                    onClick={() => handleViewDocument(filePath!)}
                />
            </div>
        )
    };
    /* *************************************************************************** */


    /* ***************************** For view file ****************************** */
    const handleViewDocument = (document: string) => {
        setFileUrl(document);
        setShowFileViewModal(true);
    }

    const handleCloseDocumentViewModal = () => {
        setShowFileViewModal(false);
    };
    /* *************************************************************************** */


    /* **************************** Common functions ***************************** */
    const goBack = () => {
        navigate(-1);
        goToTop();
    };
    /* *************************************************************************** */

    return (
        <React.Fragment>
            <Toast ref={toast} />

            <div className="main_content layout_padding show_side_bar">
                {dataLoading && initialDataloading ? (
                    <PageLoader />
                ) : errorMessage ? (
                    <ErrorPage errorMessage={errorMessage} />
                ) : (
                    <div className="page_content mt-0">
                        <div className="page_breadcrumb_area">
                            <ul className="page_breadcrumb">
                                <li className="page_breadcrumb_item has_child has_link" onClick={() => navigate(-1)}>
                                    {projectData.job_number} : {projectData.address}
                                </li>
                                <li className="page_breadcrumb_item has_child">
                                    {projectData.folder}
                                </li>
                                <li className="page_breadcrumb_item active">
                                    {projectData.feature_name}
                                </li>
                            </ul>

                            <button className="back_btn p-ripple" onClick={goBack}>
                                <i className="bi bi-arrow-left me-2"></i>
                                Back
                                <Ripple />
                            </button>
                        </div>

                        <div className="page_header_area justify-content-end mt-sm-3 mt-3">
                            <div className="page_action_area">
                                {isPermissionAddCashFlow && !isShared && (
                                    <button className="add_data_link_btn p-ripple" onClick={handleAddDetails}>
                                        <i className="bi bi-plus-circle"></i>
                                        <span>Add document</span>
                                        <Ripple />
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="page_main_content_section">
                            {cashFlowData?.length > 0 ? (
                                <DataTable
                                    value={cashFlowData}
                                    paginator
                                    size="small"
                                    rows={10}
                                    rowsPerPageOptions={[5, 10, 25, 50]}
                                    tableStyle={{ minWidth: '50rem' }}
                                    paginatorDropdownAppendTo={'self'}
                                    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                                    currentPageReportTemplate="{first} to {last} of {totalRecords}"
                                    className="page_table p-0 p-sm-2"
                                    rowHover
                                    loading={dataLoading}
                                    scrollable
                                    dataKey="id"
                                >
                                    <Column
                                        header="Document name"
                                        headerClassName="text_purple"
                                        body={(rowData, { rowIndex }) =>
                                            <span className="text-capitalize">{rowIndex + 1}.&nbsp;&nbsp;&nbsp;
                                                {rowData.document_name ? rowData.document_name : '--'}
                                            </span>
                                        }
                                        style={{ width: '40%' }}
                                    ></Column>

                                    <Column
                                        header="Uploaded date"
                                        headerClassName="text_purple"
                                        field="update_at"
                                        body={(rowData: Cashflows) => (
                                            <span className="text_no_wrap">
                                                {rowData?.file_upload_date ? rowData.file_upload_date : "---"}
                                            </span>
                                        )}
                                        style={{ width: "20%" }}
                                    ></Column>

                                    {isPermissionEditCashFlow && !isShared && (
                                        <Column
                                            header="Edit"
                                            headerClassName="text_purple"
                                            body={editDataBody}
                                            alignHeader="center"
                                            className="data_action_btn_area"
                                            style={{ width: '10%' }}
                                        ></Column>
                                    )}

                                    {isPermissionDeleteCashFlow && !isShared && (
                                        <Column
                                            header="Delete"
                                            headerClassName="text_purple"
                                            body={deleteDataBody}
                                            alignHeader="center"
                                            className="data_action_btn_area"
                                            style={{ width: '10%' }}
                                        ></Column>
                                    )}

                                    <Column
                                        body={viewDocumentBody}
                                        alignHeader="center"
                                        className="table_btn_area"
                                        style={{ width: '20%' }}
                                        frozen
                                        alignFrozen="right"
                                    ></Column>
                                </DataTable>
                            ) : (
                                <NoDataCreated
                                    message={`No ${(projectData.feature_name).toLowerCase()} document added. ${!isShared && isPermissionAddCashFlow ? 'To add the document click on add button.' : ''}`}
                                />
                            )}
                        </div>
                    </div>
                )}
            </div>


            {/* cashflow detail modal */}
            <Dialog
                header={detailModalHeader}
                footer={detailModalFooter}
                headerClassName="custom_modal_header"
                visible={showDetailModal}
                className="custom_modal_dialog modal_dialog_sm"
                onHide={handleCloseDetailModal}
                dismissableMask>
                <div className="custom_modal_body">
                    <div className="p-1 p-sm-4 pt-sm-3 pb-sm-0 w-100">
                        <div className="row">
                            {/* Document name */}
                            <div className="col-12">
                                <TextInput
                                    id="documentName"
                                    label="Document name"
                                    labelHtmlFor="documentName"
                                    required={true}
                                    value={documentName}
                                    placeholder="Document name"
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDocumentName(e.target.value)}
                                    error={documentNameError}
                                />
                            </div>

                            {/* File */}
                            <div className="col-12">
                                <FileInput
                                    id="file"
                                    label="Upload file"
                                    labelHtmlFor="file"
                                    required={dataState === 'Add' ? true : false}
                                    value={fileName}
                                    onUpload={handleFileUpload}
                                    onClear={handleClearFile}
                                    toast={toast}
                                    error={fileError}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog >
            {/*  */}


            {/* Document view modal */}
            <DocumentViewModal
                isOpen={showFileViewModal}
                onClose={handleCloseDocumentViewModal}
                fileUrl={fileUrl!}
            />
            {/*  */}
        </React.Fragment>
    )
}

export default CashFlow;